/**
 * Task to TreeNode Converter
 *
 * Converts Task data to tree structure for rc-tree
 */

import type { Task } from '@segmently/schema-models/entities';
import type { TaskTreeNode } from './types';
import { formatFieldName } from '../../shared/treeHelpers';

/**
 * Task field names to display
 */
const TASK_FIELDS = [
  'name',
  'description',
  'priority',
  'userTrustLevel',
  'requiredTimeMinutes',
] as const;

/**
 * Task array fields (collections)
 */
const TASK_ARRAY_FIELDS = [
  { name: 'funnelGoals', label: 'Funnel Goals' },
  { name: 'requiredInfo', label: 'Required Info' },
  { name: 'competitorsForClients', label: 'Competitors' },
  { name: 'userCondition', label: 'User Conditions' },
] as const;

/**
 * Converts a Task to a tree node structure
 *
 * @param task - Task entity
 * @returns Tree node for rc-tree
 */
export function taskToTreeNode(task: Task): TaskTreeNode {
  const children: TaskTreeNode[] = [];

  // Task node (checkable - can select entire task)
  const taskNode: TaskTreeNode = {
    key: `task-${task.id}`,
    title: task.name || `Task ${task.id}`,
    nodeType: 'task',
    taskId: task.id,
    checkable: true,
    selectable: false,
    children: [],
  };

  // Add basic fields
  TASK_FIELDS.forEach((fieldName) => {
    const fieldValue = task[fieldName];
    if (fieldValue !== undefined && fieldValue !== null) {
      const displayValue =
        typeof fieldValue === 'string'
          ? fieldValue.substring(0, 50)
          : String(fieldValue);

      const fieldNode: TaskTreeNode = {
        key: `task-${task.id}-${fieldName}`,
        title: `${formatFieldName(fieldName)}: ${displayValue}`,
        nodeType: 'field',
        taskId: task.id,
        fieldName,
        checkable: true,
        selectable: false,
      };
      taskNode.children!.push(fieldNode);
    }
  });

  // Add array fields (funnelGoals, requiredInfo, etc.)
  TASK_ARRAY_FIELDS.forEach(({ name: fieldName, label }) => {
    const items = task[fieldName as keyof Task] as any[];

    if (Array.isArray(items) && items.length > 0) {
      const arrayChildren: TaskTreeNode[] = items.map((item, index) => {
        let itemTitle: string;

        if (fieldName === 'funnelGoals' && typeof item === 'object' && item.goal) {
          // funnelGoals has {goal, priority}
          itemTitle = `${item.goal} (Priority: ${item.priority || 'N/A'})`;
        } else if (typeof item === 'string') {
          // Simple strings (requiredInfo, competitorsForClients, userCondition)
          itemTitle = item.substring(0, 60);
        } else if (typeof item === 'object' && item !== null) {
          // Generic object
          itemTitle = JSON.stringify(item).substring(0, 60);
        } else {
          itemTitle = `Item ${index + 1}`;
        }

        const arrayItemNode: TaskTreeNode = {
          key: `task-${task.id}-${fieldName}-${index}`,
          title: `[${index}] ${itemTitle}`,
          nodeType: 'arrayItem',
          taskId: task.id,
          fieldName,
          arrayIndex: index,
          checkable: true,
          selectable: false,
        };
        return arrayItemNode;
      });

      // Add folder node for the array
      const folderNode: TaskTreeNode = {
        key: `task-${task.id}-${fieldName}`,
        title: `${label} (${items.length})`,
        nodeType: 'folder',
        taskId: task.id,
        fieldName,
        checkable: true, // Can select entire array
        selectable: false,
        children: arrayChildren,
      };
      taskNode.children!.push(folderNode);
    }
  });

  return taskNode;
}
